// src/Services/gameControlService.js
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { systemMessageService } from "./systemMessageService";

class GameControlService {
  // Leave room function - extracted from CreateRoom.jsx
  async leaveRoom(currentRoomId, currentUserId, currentPlayerName) {
    try {
      const roomQuery = query(
        collection(db, "rooms"),
        where("roomId", "==", currentRoomId)
      );
      const roomSnapshot = await getDocs(roomQuery);

      if (!roomSnapshot.empty) {
        const roomDocRef = roomSnapshot.docs[0].ref;
        const roomData = roomSnapshot.docs[0].data();
        const roomDocId = roomSnapshot.docs[0].id;

        const updatedPlayers = roomData.players.filter(
          (player) => player.id !== currentUserId
        );

        await updateDoc(roomDocRef, {
          players: updatedPlayers,
        });

        // Send system message when player leaves
        if (currentPlayerName && roomDocId) {
          try {
            await systemMessageService.playerLeft(roomDocId, currentPlayerName);
          } catch (error) {
            console.warn("Failed to send leave message:", error);
          }
        }

        // Clear localStorage
        localStorage.removeItem("currentRoomId");
        localStorage.removeItem("currentUserId");
        localStorage.removeItem("playerName");
        localStorage.removeItem("isHost");

        return { success: true };
      }

      return { success: false, message: "Room not found" };
    } catch (error) {
      console.error("Error leaving room:", error);
      return { success: false, message: "Failed to leave room" };
    }
  }

  // Remove player function - extracted from CreateRoom.jsx
  async removePlayer(currentRoomId, playerId, roomData, roomDocId) {
    try {
      if (!roomData || !roomDocId) return { success: false };

      // Get player name before removing
      const playerToRemove = roomData.players.find((p) => p.id === playerId);
      const playerName = playerToRemove?.name;

      const roomQuery = query(
        collection(db, "rooms"),
        where("roomId", "==", currentRoomId)
      );
      const roomSnapshot = await getDocs(roomQuery);

      if (!roomSnapshot.empty) {
        const roomDocRef = roomSnapshot.docs[0].ref;

        const updatedPlayers = roomData.players.filter(
          (player) => player.id !== playerId
        );

        await updateDoc(roomDocRef, {
          players: updatedPlayers,
        });

        // Send system message about removal
        if (playerName && roomDocId) {
          try {
            const hostName = localStorage.getItem("playerName");
            await systemMessageService.playerRemoved(
              roomDocId,
              playerName,
              hostName
            );
          } catch (error) {
            console.warn("Failed to send removal message:", error);
          }
        }

        return { success: true };
      }

      return { success: false, message: "Room not found" };
    } catch (error) {
      console.error("Error removing player:", error);
      return { success: false, message: "Failed to remove player" };
    }
  }

  // End game for all players
  async endGameForAll(roomDocId, currentUserId, reason = "player_left") {
    try {
      if (!roomDocId) return { success: false };

      const roomRef = doc(db, "rooms", roomDocId);
      await updateDoc(roomRef, {
        gameStatus: "ended",
        gameEndedAt: new Date(),
        gameEndedBy: currentUserId,
        gameEndReason: reason,
      });

      // Send system message about game ending
      try {
        const playerName = localStorage.getItem("playerName");
        if (reason === "player_left") {
          await systemMessageService.gameEndedDueToPlayerLeaving(
            roomDocId,
            playerName
          );
        } else {
          await systemMessageService.gameEndedByHost(roomDocId, playerName);
        }
      } catch (error) {
        console.warn("Failed to send game end message:", error);
      }

      return { success: true };
    } catch (error) {
      console.error("Error ending game:", error);
      return { success: false, message: "Failed to end game" };
    }
  }

  // Check if player can leave (2-player rule)
  canPlayerLeave(roomData, currentUserId) {
    const playerCount = roomData?.players?.length || 0;
    const isHost = roomData?.hostId === currentUserId;
    const gameInProgress = roomData?.gameStatus === "playing" || roomData?.gameStatus === "starting";

    return {
      canLeave: true, // Always allow leaving, but with different consequences
      playerCount,
      isHost,
      gameInProgress,
      willEndGame: playerCount <= 2 && gameInProgress,
    };
  }

  // Delete entire room (host only)
  async deleteRoom(currentRoomId) {
    try {
      const roomQuery = query(
        collection(db, "rooms"),
        where("roomId", "==", currentRoomId)
      );
      const roomSnapshot = await getDocs(roomQuery);

      if (!roomSnapshot.empty) {
        const roomDoc = roomSnapshot.docs[0];
        await deleteDoc(doc(db, "rooms", roomDoc.id));

        // Clear localStorage
        localStorage.removeItem("currentRoomId");
        localStorage.removeItem("currentUserId");
        localStorage.removeItem("playerName");
        localStorage.removeItem("isHost");

        return { success: true };
      }

      return { success: false, message: "Room not found" };
    } catch (error) {
      console.error("Error deleting room:", error);
      return { success: false, message: "Failed to delete room" };
    }
  }
}

// Export singleton instance
export const gameControlService = new GameControlService();
export default gameControlService;